import { FC, useEffect, useState } from "react";
import { useLocales, useUser } from "../../hooks";
import { IOption } from "../../interfaces/Utils";
import { useFormikContext } from "formik";
import { AutocompleteDropDown } from "..";
import {
  locale,
  workOrderStatusEnum,
  workOrderStatusOptions,
  workOrderTypeEnum,
} from "../../utils";
import { WorkOrderChangeStatusEnum } from "../../utils/enum";

interface IWorkOrderStatus {
  creatorId: string;
  disabled: boolean;
}

const WorkOrderStatusInput: FC<IWorkOrderStatus> = ({
  creatorId,
  disabled,
}) => {
  const { translate } = useLocales();
  const { user } = useUser();
  const [options, setOptions] = useState<IOption | any>([]);
  const { values, mainGroup }: any = useFormikContext();
  const isOwner = values.responsibleUserId === user.id;
  const isCreator = user.id === creatorId;

  useEffect(() => {
    const customOptions = statusOptionRules(
      values,
      isOwner,
      isCreator,
      mainGroup
    );
    setOptions(customOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.responsibleUserId]);

  if (options.length < 1) {
    return null;
  }

  return (
    <AutocompleteDropDown
      name="statusId"
      label={translate("resources.workOrders.statusId")}
      options={options}
      disabled={disabled}
    />
  );
};

const statusOptionRules = (
  values: any,
  isOwner: boolean,
  isCreator: boolean,
  mainGroup: any
) => {
  const workorderTypeId = values.typeId;
  const changeStatusId = values.detail?.changeStatusId;

  switch (workorderTypeId) {
    case workOrderTypeEnum.findingAndSuggestions || workOrderTypeEnum.dif:
      if (isCreator) {
        const options = workOrderStatusOptions.map((status: any) => ({
          value: status.value,
          label: status.label,
        }));
        const localeOptions = locale(options, "resources.workOrders.");
        return localeOptions;
      }
      // check is owner
      else if (isOwner) {
        const foreignOptions = workOrderStatusOptions
          .filter(
            (status: any) =>
              status.value === values.statusId ||
              status.value === workOrderStatusEnum.workingOnIt ||
              status.value === workOrderStatusEnum.resolved ||
              status.value === workOrderStatusEnum.closed
          )
          .map((option: any) => ({ value: option.value, label: option.label }));

        return locale(foreignOptions, "resources.workOrders.");
      }
      // normal person
      else {
        const foreignOptions = workOrderStatusOptions
          .filter((status: any) => status.value === values.statusId)
          .map((option: any) => ({ value: option.value, label: option.label }));

        return locale(foreignOptions, "resources.workOrders.");
      }
    case workOrderTypeEnum.change:
      if (changeStatusId === WorkOrderChangeStatusEnum.NotApproved) {
        const foreignOptions = workOrderStatusOptions
          .filter((status: any) => status.value === values.statusId)
          .map((option: any) => ({ value: option.value, label: option.label }));

        return locale(foreignOptions, "resources.workOrders.");
      } else {
        const options = workOrderStatusOptions.map((status: any) => ({
          value: status.value,
          label: status.label,
        }));
        const localeOptions = locale(options, "resources.workOrders.");
        return localeOptions;
      }
    default:
      if (isOwner) {
        // law rules, if workorder belongs to law department then only law department members close this workorder
        if (
          !(
            mainGroup?.groupName?.trim() === "Hukuk ve Regülasyonlar" ||
            mainGroup?.groupName?.trim() ===
              "Hukuk ve Regülasyonlar Müdürlüğü" ||
            mainGroup?.groupName?.trim() ===
              "Hukuk ve Regülasyonlar Grup Müdürlüğü"
          ) &&
          values.isTheLawDepartmentOpenThisWorkOrder &&
          values.typeId === workOrderTypeEnum.request
        ) {
          const lawWorkOrderOptions = workOrderStatusOptions
            .filter(
              (status: any) =>
                status.value === values.statusId ||
                status.value === workOrderStatusEnum.workingOnIt ||
                status.value === workOrderStatusEnum.resolved ||
                status.value === workOrderStatusEnum.onHold
            )
            .map((option: any) => ({
              value: option.value,
              label: option.label,
            }));

          return locale(lawWorkOrderOptions, "resources.workOrders.");
        } else {
          const options = workOrderStatusOptions.map((status: any) => ({
            value: status.value,
            label: status.label,
          }));
          const localeOptions = locale(options, "resources.workOrders.");
          return localeOptions;
        }
      } else {
        const foreignOptions = workOrderStatusOptions
          .filter(
            (status: any) =>
              status.value === values.statusId ||
              status.label === workOrderStatusEnum.cancelled
          )
          .map((option: any) => ({ value: option.value, label: option.label }));

        return locale(foreignOptions, "resources.workOrders.");
      }
  }
};

export default WorkOrderStatusInput;
